import { EMAIL_REGEX, PASSWORD_REQUIREMENTS } from "@/constants";
import i18n from "@/i18n";
import { PasswordValidation, RegionConfig, UserData, WowAccount } from "@/types";
import { BadgeColor, EnhancedAuctionItem } from "@/types/domain/auction";
import { LanguageCode, getLocaleURL } from "./languageConstants";

export const capitalize = (str: string): string => {
  if (!str) return str;
  return str[0].toUpperCase() + str.slice(1);
};

export const sortByPatch = (items: EnhancedAuctionItem[]) =>
  items.sort((a, b) => {
    if (a.patchNumber === null) return 1;
    if (b.patchNumber === null) return -1;
    return b.patchNumber - a.patchNumber;
  });

export const formatGold = (amount: number): string => `${Number(Math.trunc(amount)).toLocaleString()}g`;

export const formatPercentage = (value: number, fallback = 9999999): string => {
  const percentage = Math.trunc(value);
  return `${isNaN(percentage) ? fallback : Math.min(fallback, percentage)}%`;
};

export const truncateRealmName = (realmName: string, maxLength = 15): string => {
  const commaIndex = realmName.indexOf(',');
  if (commaIndex !== -1 && commaIndex < maxLength) {
    return realmName.substring(0, commaIndex + 2) + '...';
  }
  return realmName.length > maxLength ? realmName.substring(0, maxLength) + '...' : realmName;
};

export const getApiBaseUrl = (): string => {
  return import.meta.env.MODE === "development"
    ? import.meta.env.VITE_BASE_BACK_URL_DEV
    : import.meta.env.VITE_BASE_BACK_URL_PROD;
};

// Feature flag: WoW Classic support. Off unless VITE_CLASSIC_ACTIVE="true",
// mirroring the backend's CLASSIC_ACTIVE flag.
export const isClassicActive = (): boolean =>
  import.meta.env.VITE_CLASSIC_ACTIVE === "true";

export const validatePassword = (password: string): PasswordValidation => {
  const errors: string[] = [];

  if (!password) {
    errors.push(i18n.t('errors:validation.passwordRequired'));
    return { isValid: false, errors };
  }

  if (password.length < PASSWORD_REQUIREMENTS.minLength) {
    errors.push(i18n.t('errors:validation.passwordMinLength', { count: PASSWORD_REQUIREMENTS.minLength }));
  }

  if (!PASSWORD_REQUIREMENTS.hasUppercase.test(password)) {
    errors.push(i18n.t('errors:validation.passwordUppercase'));
  }

  if (!PASSWORD_REQUIREMENTS.hasLowercase.test(password)) {
    errors.push(i18n.t('errors:validation.passwordLowercase'));
  }

  if (!PASSWORD_REQUIREMENTS.hasNumber.test(password)) {
    errors.push(i18n.t('errors:validation.passwordNumber'));
  }

  if (!PASSWORD_REQUIREMENTS.hasSpecialChar.test(password)) {
    errors.push(i18n.t('errors:validation.passwordSpecialChar'));
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

export const validateConfirmPassword = (password: string, confirmPassword: string): string[] => {
  const errors: string[] = [];

  if (!confirmPassword) {
    errors.push(i18n.t('errors:validation.confirmPasswordRequired'));
  } else if (password !== confirmPassword) {
    errors.push(i18n.t('errors:validation.passwordsDoNotMatch'));
  }

  return errors;
};

export const validateEmail = (email: string): string[] => {
  const errors: string[] = [];

  if (!email) {
    errors.push(i18n.t('errors:validation.emailRequired'));
  } else if (!EMAIL_REGEX.test(email)) {
    errors.push(i18n.t('errors:validation.emailInvalid'));
  }

  return errors;
};


export const getWowheadUrl = (item: EnhancedAuctionItem, locale: LanguageCode = 'enUS'): string => {
  const isPet = typeof item.id === 'string' && item.id.startsWith('pet');
  const lang = getLocaleURL(locale)
  const baseUrl = `https://www.wowhead.com/${lang}${isPet ? 'npc' : 'item'}=`;
  const itemId = isPet ? item.creatureId : item.id;
  // Bonus IDs only, deliberately no ilvl override: the wowhead tooltip acts
  // as an independent check of our own item level calculation. Known limits:
  // wowhead shows the max-scaled level for drop-level-scaled listings (the
  // drop level lives in an auction modifier, not a bonus ID) and does not
  // squish pre-Midnight fixed-level bonuses.
  const bonus = (item.bonus?.length > 0) ? `?bonus=${item.bonus.join(':')}` : ''
  return `${baseUrl}${itemId}${bonus}`;
};

export const getCustomCategory = (item: EnhancedAuctionItem): string => {
  const { subclass, class: itemClass } = item;

  if (['Toys', 'Mount', 'Companions'].includes(subclass)) {
    return subclass;
  }

  if (itemClass === 'Miscellaneous' && (subclass === 'Other' || subclass === 'Holiday')) {
    return 'Misc';
  }

  return itemClass;
};

export const getSaleRateColor = (saleRate: number): string => {
  if (saleRate < 0.005) return 'text-red-500';
  if (saleRate < 0.02) return 'text-yellow-500';
  return 'text-green-500';
};

export const getPopulationColor = (population: string): BadgeColor => {
  if (population === "Low") return 'green';
  if (population === "Medium") return 'yellow';
  if (population === "High") return 'red';
  if (population === "Full") return 'red';
  return 'blue';
}

export const populationWeight = (name: string): number => {
  if (name === "Full") return 5;
  if (name === "High") return 4;
  if (name === "Medium") return 3;
  if (name === "Low") return 2;
  return 1;
}

export const betweenNumber = (val: number, check: [number, number], origMax: number): boolean => {
  const min = Math.min(check[0], check[1]),
    max = Math.max(check[0], check[1]);

  if (max == origMax) {
    return val >= min
  }

  return val >= min && val <= max;
};

export const getRegionConfig = (userData: UserData): RegionConfig => {
  const isUS = userData.region === 'us';
  return isUS ? userData.retailConfigUS : userData.retailConfigEU;
}

export const getAccountNameByRealmId = (
  userData: UserData,
  realmId: number,
  type: 'selling' | 'buying' = 'selling'
): WowAccount | undefined => {
  if (!userData) return undefined;

  // Get the appropriate region config based on user's region
  const regionConfig = getRegionConfig(userData);

  // Select the correct realm list based on type
  const realmList = type === 'selling'
    ? regionConfig.sellingRealms
    : regionConfig.buyingRealms;

  // Find the realm config that matches this realmId
  const realmConfig = realmList.find(rc => rc.realmId === realmId);

  if (!realmConfig) return undefined;

  return userData.wowAccounts.find(
    account => account.id === realmConfig.accountId
  );
};

export const isBasic = (accessType: string | undefined): boolean => {
  return accessType === "basic"
}

export const isPremium = (accessType: string | undefined): boolean => {
  return accessType === "premium"
}

export const isAllowed = (accessType: string | undefined): boolean => {
  return accessType === "premium" || accessType === "trial"
}

// Snipe price constants (copper)
export const MIN_SNIPE_PRICE_COPPER = 10000; // 1g
export const MAX_SNIPE_PRICE_COPPER = 99999999900; // 9,999,999g 99s

export const copperToGoldString = (copper: number): string => {
  const gold = Math.floor(copper / 10000);
  const silver = Math.floor((copper % 10000) / 100);
  const parts: string[] = [];
  if (gold > 0) parts.push(`${gold.toLocaleString("en-US")}g`);
  if (silver > 0 || parts.length === 0) parts.push(`${silver}s`);
  return parts.join(" ");
};

export const parseGoldInput = (input: string): number | null => {
  const trimmed = input.trim();
  if (!trimmed) return null;

  // Pattern: Xg Ys (any combination, no copper)
  const gMatch = trimmed.match(/(\d[\d,]*)g/i);
  const sMatch = trimmed.match(/(\d+)s/i);

  if (gMatch || sMatch) {
    const gold = gMatch ? parseInt(gMatch[1].replace(/,/g, ""), 10) : 0;
    const silver = sMatch ? Math.min(parseInt(sMatch[1], 10), 99) : 0;
    return gold * 10000 + silver * 100;
  }

  // Plain number = gold
  const num = parseFloat(trimmed.replace(/,/g, ""));
  if (!isNaN(num) && num > 0) {
    const copper = Math.round(num * 10000);
    return Math.floor(copper / 100) * 100;
  }

  return null;
};